import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import React, { useState } from "react";

import { Client, CustomField } from "0.Mine/3.Interfaces/Client";

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (client: Omit<Client, "id">) => Promise<void>;
  customFields: CustomField[];
}

export function AddClientModal({ isOpen, onClose, onAdd, customFields }: AddClientModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<Omit<Client, "id">>({
    name: "",
    email: "",
    phone: "",
    company: "",
    status: "active",
    lastContact: new Date().toISOString().slice(0, 10),
    value: 0,
    tags: [],
    customFields: {},
  });
  const [tagInput, setTagInput] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    if (!name) return;
    setFormData((prev) => ({
      ...prev,
      [name]: value as any,
    }));
  };

  const handleTagInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTagInput(e.target.value);
  };

  const addTag = () => {
    if (tagInput.trim()) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  const removeTag = (tagToDelete: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToDelete),
    }));
  };

  const handleCustomFieldChange = (id: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [id]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onAdd(formData);
      onClose();
    } catch (error) {
      console.error("Errore aggiunta cliente:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Aggiungi Nuovo Cliente</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ borderRadius: "0!important" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Nome"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              label="Telefono"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Azienda"
              name="company"
              value={formData.company}
              onChange={handleChange}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="status-label">Stato</InputLabel>
              <Select
                labelId="status-label"
                label="Stato"
                name="status"
                value={formData.status}
                //onChange={handleChange}
                fullWidth
              >
                <MenuItem value="active">Attivo</MenuItem>
                <MenuItem value="inactive">Inattivo</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Ultimo contatto"
              name="lastContact"
              type="date"
              value={formData.lastContact}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Valore (€)"
              name="value"
              type="number"
              value={formData.value}
              onChange={handleChange}
              fullWidth
            />
          </Box>

          {customFields.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ color: "#36376d", marginBottom: "30px" }}
              >
                Campi Personalizzati
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                {customFields.map((field) => (
                  <Box key={field.id}>
                    <InputLabel sx={{ padding: "8px 5px 0px 16px" }}>{field.name}</InputLabel>
                    {field.type === "select" ? (
                      <FormControl fullWidth>
                        <Select
                          value={formData.customFields[field.id] || ""}
                          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="">Seleziona opzione</MenuItem>
                          {field.options?.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : field.type === "date" ? (
                      <TextField
                        type="date"
                        value={formData.customFields[field.id] || ""}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    ) : (
                      <TextField
                        type={field.type === "number" ? "number" : "text"}
                        value={formData.customFields[field.id] || ""}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                        fullWidth
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ gridColumn: { md: "1 / -1" }, margin: "29px 1rem" }}>
          <TextField
            label="Aggiungi Tag"
            value={tagInput}
            onChange={handleTagInputChange}
            onKeyDown={handleTagKeyDown}
            fullWidth
            helperText="Premi Enter per aggiungere un tag"
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              mt: 1,
              flexWrap: "wrap",
              gap: 1,
              "& > *": {
                mb: 1,
              },
            }}
          >
            {formData.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                onDelete={() => removeTag(tag)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : "Aggiungi Cliente"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
